import React from 'react';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import TaskItem from '@tiptap/extension-task-item';

import { NodeViewProps } from '@tiptap/core';
import { TaskItemOptions } from '@tiptap/extension-task-item/src/task-item';

import {
    createNewEmptyTask,
    createNewEmptyTaskAbove,
    handleEmptyFirstTaskDeletion,
    handleEmptyFirstTaskToTitle,
    handleEmptyPreviousTaskDeletion,
    handleEmptyTaskForwardDeletion,
    handleFirstTitledTaskBackspace,
    handleModifiedDeleteShortcut,
    handleTaskItemAssignmentShortcut,
    handleTaskItemWithNestedPreviousItem,
    TaskItemAssignmentShortcutContext,
} from './taskListItemKeyboardHandlers';
import { createTaskToTitleInputRule } from './taskListTitleInputRules';
import TaskListItemDeleteButton from './TaskListItemDeleteButton';
import TaskListItemDragHandle, { TaskItemDragHandleComponentProps } from './TaskListItemDragHandle';
import TaskListItemActivityIndicator, {
    TaskItemActivityIndicatorComponentProps,
} from './TaskListItemActivityIndicator';
import { createTaskItemDragSourcePlugin } from './taskListItemDragSourcePlugin';
import { createTaskListTextSelectionPlugin } from './taskListTextSelectionPlugin';
import { deleteTaskItem, TaskDeletionInfo } from './taskListItemDeletionUtils';
import { setTaskItemCheckedWithSubTasks } from './taskListItemAttrUtils';
import { Checkbox } from '@milanote/roma';
import type { TaskItemChipAttrs } from './taskListItemChipTypes';

/**
 * Props for the injected chips renderer. Typed only against `src/common` attr
 * types so the concrete `src/client` chips component can be injected via options
 * without `src/common` importing `src/client` (mirrors the checkbox/delete pattern).
 */
export type TaskItemChipsComponentProps = {
    attrs: TaskItemChipAttrs & { checked?: boolean };
    updateAttributes: (attrs: Partial<TaskItemChipAttrs>) => void;
    /** Whether the chips can be modified (from the owning board's write permission). */
    canModify?: boolean;
    /** The owning canvas element whose single-selection gates chip activation. */
    elementId: string;
};

type TaskListItemOptions = TaskItemOptions & {
    DeleteIconComponent: React.ComponentType;
    DragHandleComponent: React.ComponentType<TaskItemDragHandleComponentProps>;
    ChipsComponent: React.ComponentType<TaskItemChipsComponentProps>;
    /**
     * Renders the task's in-board activity badge (unseen assignment/reaction/label activity).
     * Optional so configurations that don't need it (e.g. Storybook) can omit it.
     */
    ActivityIndicatorComponent?: React.ComponentType<TaskItemActivityIndicatorComponentProps>;
    // Whether the task items can be modified (chips edited, items dragged out),
    // derived from the owning board's write permission where the editor's extensions
    // are configured. Unlike the editor's `isEditable` (which flips as the user
    // enters/leaves editing), this is stable for the editor's lifetime, so it can
    // be a static extension option.
    canModifyContent?: boolean;
    elementId: string;
    isElementSingleSelected?: boolean;
    onDeleteTask?: (info: TaskDeletionInfo) => void;
    /**
     * Opens the assignee picker for the task the "@" shortcut fired on (supplied
     * by the client, which owns the chip selection state). The shortcut is
     * inactive when omitted.
     */
    onAssignmentShortcut?: (context: TaskItemAssignmentShortcutContext) => void;
};

/**
 * Renders a task list item: the Roma checkbox plus the injected (client-supplied)
 * delete icon, drag handle, and chips components.
 */
const TaskListItemDisplay: React.FC<NodeViewProps> = (props) => {
    const { node, editor, getPos, updateAttributes, extension } = props;
    const {
        DeleteIconComponent,
        DragHandleComponent,
        ChipsComponent,
        ActivityIndicatorComponent,
        canModifyContent,
        elementId,
        isElementSingleSelected,
        onDeleteTask,
    } = extension.options;

    // Shift-click makes the clicked task the master: its new checked state is applied
    // to all of its nested sub-tasks as well.
    const onClick = (event: React.MouseEvent) => {
        event.stopPropagation();

        const checked = !node.attrs.checked;

        if (event.shiftKey) {
            setTaskItemCheckedWithSubTasks({ editor, position: getPos(), checked });
            return;
        }

        updateAttributes({ checked });
    };

    /**
     * Updates task-item chip data and persists the containing task list immediately.
     */
    const updateChipAttributes = (attrs: Partial<TaskItemChipAttrs>) => {
        updateAttributes(attrs);
        editor.commands.flushPendingUpdate({});
    };

    const onDelete = () => deleteTaskItem(editor, getPos(), onDeleteTask);

    return (
        <NodeViewWrapper className="task-item-row" data-task-id={node.attrs.taskId}>
            {/*
                The Roma checkbox is presentational, so the click-to-toggle lives on this
                non-editable wrapper (ProseMirror treats it as node chrome). `display: contents`
                keeps the checkbox's own `.RomaCheckboxBase` element as the row's flex child.
            */}
            <div contentEditable={false} onClick={onClick} style={{ display: 'contents' }}>
                <Checkbox.Base
                    className="hit-slop-t-md hit-slop-r-sm hit-slop-b-md hit-slop-l-xl"
                    isSelected={node.attrs.checked}
                    variant="tertiary"
                />
            </div>
            {/*
                Display-only badge, rendered as node chrome (see `TaskListItemActivityIndicator`)
                so it can never steal focus, clicks, or the caret from the editor.
            */}
            {ActivityIndicatorComponent && (
                <TaskListItemActivityIndicator
                    ActivityIndicatorComponent={ActivityIndicatorComponent}
                    taskId={node.attrs.taskId}
                    isSelected={isElementSingleSelected}
                />
            )}
            {/*
                Rendered before `.task-item-main` (which holds nested sub-task rows and their own end-actions)
                so that, with equal z-index, this row's actions paint *below* any nested row's actions.
                That lets the deepest hovered row's gutter win the hover hit-test (see TaskListEditor.scss).
                Non-editable so ProseMirror treats it as node chrome, not content.
            */}
            <div className="task-item-end-actions" contentEditable={false}>
                <TaskListItemDeleteButton
                    show={node.attrs.checked}
                    DeleteIcon={DeleteIconComponent}
                    onDelete={onDelete}
                />
                <TaskListItemDragHandle
                    DragHandleComponent={DragHandleComponent}
                    editor={editor}
                    elementId={elementId}
                    node={node}
                    getPos={getPos}
                    canDrag={!!canModifyContent}
                />
            </div>
            <div className="task-item-main">
                <NodeViewContent className="task-item-content" />
                <div
                    className="task-item-chips"
                    contentEditable={false}
                    // Outside NodeViewContent and non-editable so ProseMirror treats the
                    // chips as node chrome; stop pointer propagation so clicking a chip
                    // does not move the editor selection (mirrors the checkbox pattern).
                    onMouseDown={(event) => {
                        if (isElementSingleSelected) event.stopPropagation();
                    }}
                >
                    <ChipsComponent
                        attrs={node.attrs as TaskItemChipsComponentProps['attrs']}
                        updateAttributes={updateChipAttributes}
                        canModify={canModifyContent}
                        elementId={elementId}
                    />
                </div>
            </div>
        </NodeViewWrapper>
    );
};

/**
 * Decides whether ProseMirror should ignore an event that occurred inside a task item
 * node view (`true` = the node view owns the event, ProseMirror ignores it).
 *
 * Replaces the default `NodeView.stopEvent` for one reason: drags that start from the
 * item's drag handle belong to react-dnd, but the default preventDefaults every drag
 * event on a non-draggable node, which cancels the native HTML5 drag before react-dnd
 * can begin it. Everything else replicates the default's behaviour for this node
 * (non-draggable, selectable), using the node view's class names in place of its
 * dom/contentDOM references (which a custom stopEvent doesn't receive).
 */
export const stopTaskItemNodeViewEvent = ({ event }: { event: Event }): boolean => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest) return false;

    const isDragEvent = event.type.startsWith('drag');

    // Drags from the drag handle are react-dnd's: claim the event without
    // preventDefault-ing it so the native drag can begin.
    if (isDragEvent && target.closest('.task-list-item-drag-handle-container')) return true;

    // The nearest of the content hole / chrome regions tells us where the event
    // happened. Anything in the editable content hole is ProseMirror's (the default's
    // `isInElement` check); anything else in the row is node view chrome.
    const nearestRegion = target.closest('.task-item-content, .task-item-end-actions, .task-item-chips');
    if (nearestRegion?.classList.contains('task-item-content')) return false;

    const isDropEvent = event.type === 'drop';
    const isInput = ['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable;
    if (isInput && !isDropEvent && !isDragEvent) return true;

    // Stop ProseMirror from dragging the (selectable, non-draggable) node itself,
    // e.g. via a text selection drag that starts on chrome.
    if (isDragEvent) {
        event.preventDefault();
        return true;
    }

    // These events are handled by ProseMirror.
    const isHandledByProsemirror =
        isDropEvent ||
        event.type === 'copy' ||
        event.type === 'paste' ||
        event.type === 'cut' ||
        event.type === 'mousedown';

    return !isHandledByProsemirror;
};

export const TaskListItem = TaskItem.extend<TaskListItemOptions>({
    addAttributes() {
        return {
            // Preserve the base task item attrs (e.g. `checked`).
            ...this.parent?.(),
            // Per-task chip data. `keepOnSplit: false` so pressing Enter yields a blank
            // task rather than inheriting the previous task's due date/labels/reactions.
            // These attrs are in-document only (`rendered: false`); this POC does not
            // persist or HTML-serialise chip data.
            // The stable `taskId` is supplied separately by the `UniqueID` extension
            // (configured where the editor composes its extensions), mirroring the card editor.
            dueDate: { default: null, keepOnSplit: false, rendered: false },
            assignments: { default: [], keepOnSplit: false, rendered: false },
            labels: { default: [], keepOnSplit: false, rendered: false },
            reactions: { default: [], keepOnSplit: false, rendered: false },
            // Gates the empty "Assign to" placeholder chip (mirrors the element
            // `showAssignments` flag): set when the Assign tool is used so the
            // placeholder persists even before an assignee is chosen, and stays
            // shown if the user dismisses the popup without picking anyone.
            showAssignments: { default: false, keepOnSplit: false, rendered: false },
            // Gates the empty "Set due date" placeholder chip (mirrors the element
            // `showDueDate` flag): set when the Due date tool is used so the
            // placeholder persists even before a date is chosen, and stays shown
            // if the user dismisses the popup without picking one.
            showDueDate: { default: false, keepOnSplit: false, rendered: false },
        };
    },

    addInputRules() {
        // Convert an empty first task into a title when "# " is typed (preserving any base rules).
        return [...(this.parent?.() ?? []), createTaskToTitleInputRule()];
    },

    addNodeView() {
        return ReactNodeViewRenderer(TaskListItemDisplay, {
            as: 'li',
            attrs: ({ node }) => ({
                'data-checked': String(node.attrs.checked),
            }),
            stopEvent: stopTaskItemNodeViewEvent,
        });
    },

    addProseMirrorPlugins() {
        return [
            // Preserve any base plugins.
            ...(this.parent?.() ?? []),
            // Fades the source item while it's being dragged out of the list
            createTaskItemDragSourcePlugin(),
            // Keeps the rows' hover actions out of the way of text selections
            createTaskListTextSelectionPlugin(),
        ];
    },

    addKeyboardShortcuts() {
        const triggerModifiedDeleteShortcut = () =>
            this.editor.commands.first((props) => [() => handleModifiedDeleteShortcut(props, this.name)]);

        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            Backspace: () =>
                this.editor.commands.first((props) => [
                    () => handleEmptyFirstTaskToTitle(props, this.name),
                    () => handleFirstTitledTaskBackspace(props, this.name),
                    () => handleEmptyFirstTaskDeletion(props, this.name),
                    () => handleEmptyPreviousTaskDeletion(props, this.name),
                    () => handleTaskItemWithNestedPreviousItem(props, this.name),
                ]),
            Delete: () =>
                this.editor.commands.first((props) => [() => handleEmptyTaskForwardDeletion(props, this.name)]),
            Enter: () =>
                this.editor.commands.first((props) => [
                    () => createNewEmptyTaskAbove(props, this.name),
                    () => props.commands.splitListItem(this.name),
                    () => createNewEmptyTask(props, this.name),
                ]),
            'Mod-Delete': triggerModifiedDeleteShortcut,
            'Alt-Delete': triggerModifiedDeleteShortcut,
            'Mod-Backspace': triggerModifiedDeleteShortcut,
            'Alt-Backspace': triggerModifiedDeleteShortcut,
            '@': () =>
                this.editor.commands.first((props) => [
                    () => handleTaskItemAssignmentShortcut(props, this.name, this.options.onAssignmentShortcut),
                ]),
            'Shift-Tab': () => {
                this.editor.commands.liftListItem(this.name);
                // Intentionally always return true to prevent focus from leaving the editor
                return true;
            },
            Tab: () => {
                this.editor.commands.sinkListItem(this.name);
                // Intentionally always return true to prevent focus from leaving the editor
                return true;
            },
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    },
});
